import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { household, goBack, showToast, friendlyError, bumpScheduleVersion } from '../store.js';
import { __ } from '../i18n.js';
import {
  WEEKDAYS,
  hasWeekday,
  toggleWeekday,
  weekdaysSummary,
  SCHEDULE_ICON_CHOICES,
  SCHEDULE_FALLBACK_ICON,
  scheduleIconName,
  WARN_OPTIONS,
} from '../lib/schedule.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

/**
 * HH-6: one form for both kinds of schedule item (plan §4.3's "one form
 * handles both with a herhaalt wekelijks / eenmalig toggle").
 *
 * There are exactly two kinds and there will not be a third: no monthly,
 * no "elke tweede dinsdag", no RRULE — plan §4.3 calls that the most
 * important skip in the whole plan, and HH-6's acceptance criterion 3
 * makes its absence checkable. The toggle below is the entire recurrence
 * model.
 *
 * Fields the chosen kind has no meaning for are hidden here and cleared
 * server-side (ETM_Schedule::normalize_for_kind()), so converting a
 * weekly item into a one-off can't leave a stale bitmask behind that
 * would resurrect the series later.
 */
export default function ScheduleFormScreen({ params }) {
  const itemId = params && params.itemId;
  const isEdit = Boolean(itemId);

  // The tapped day, handed over by WeekScreen. `weekdayBit` comes from the
  // server's own week payload rather than being derived from the date
  // here — see ETM_Schedule::week()'s note on Sunday-first getDay().
  const presetDate = (params && params.date) || '';
  const presetWeekdayBit = (params && params.weekdayBit) || 0;

  const [loading, setLoading] = useState(isEdit);
  const [kind, setKind] = useState('weekly');
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState(SCHEDULE_FALLBACK_ICON);
  const [weekdays, setWeekdays] = useState(presetWeekdayBit);
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [occursAt, setOccursAt] = useState(presetDate ? `${presetDate}T09:00` : '');
  const [personId, setPersonId] = useState('');
  const [warnMinutes, setWarnMinutes] = useState('');
  const [activeFrom, setActiveFrom] = useState('');
  const [activeUntil, setActiveUntil] = useState('');
  const [isShared, setIsShared] = useState(!!household.value);
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.people
      .list()
      .then((res) => {
        if (!cancelled) setPeople(res.people || []);
      })
      .catch(() => {
        // The roster is optional context (an item without a person is a
        // whole-household item); a failure here must not block the form.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;

    api.schedule
      .get(itemId)
      .then((res) => {
        if (cancelled) return;
        const item = res.item;
        setKind(item.kind);
        setTitle(item.title || '');
        setIcon(scheduleIconName(item.icon));
        setWeekdays(item.weekdays || 0);
        setTimeStart(item.time_start || '');
        setTimeEnd(item.time_end || '');
        setOccursAt(item.occurs_at ? item.occurs_at.replace(' ', 'T') : '');
        setPersonId(item.person_id ? String(item.person_id) : '');
        setWarnMinutes(item.warn_minutes_before === null ? '' : String(item.warn_minutes_before));
        setActiveFrom(item.active_from || '');
        setActiveUntil(item.active_until || '');
      })
      .catch((err) => {
        if (cancelled) return;
        showToast(friendlyError(err), 'error');
        goBack();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [itemId]);

  function buildPayload() {
    const payload = {
      kind,
      title: title.trim(),
      icon,
      person_id: personId === '' ? '' : Number(personId),
      warn_minutes_before: warnMinutes === '' ? '' : Number(warnMinutes),
    };

    if (kind === 'weekly') {
      payload.weekdays = weekdays;
      payload.time_start = timeStart;
      payload.time_end = timeEnd;
      payload.active_from = activeFrom;
      payload.active_until = activeUntil;
    } else {
      payload.occurs_at = occursAt;
    }

    return payload;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!title.trim()) {
      showToast(__('Geef het item een naam.', 'etm'), 'error');
      return;
    }
    // Checked here as well as server-side so the common slip costs no
    // round trip; the backend keeps its own guard either way.
    if (kind === 'weekly' && !weekdays) {
      showToast(__('Kies minstens één dag.', 'etm'), 'error');
      return;
    }
    if (kind === 'once' && !occursAt) {
      showToast(__('Kies een datum en tijd.', 'etm'), 'error');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.schedule.update(itemId, buildPayload());
        showToast(__('Item opgeslagen.', 'etm'), 'success');
      } else {
        const payload = buildPayload();
        payload.is_shared = isShared;
        await api.schedule.create(payload);
        showToast(__('Item toegevoegd.', 'etm'), 'success');
      }
      bumpScheduleVersion();
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(__('Dit item uit het weekritme verwijderen?', 'etm'))) return;
    try {
      await api.schedule.remove(itemId);
      showToast(__('Item verwijderd.', 'etm'), 'success');
      bumpScheduleVersion();
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  if (loading) {
    return (
      <div>
        <BackButton onClick={goBack} />
        <p class="etm-field-hint">{__('Laden…', 'etm')}</p>
      </div>
    );
  }

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">
        {isEdit ? __('Item bewerken', 'etm') : __('Nieuw item', 'etm')}
      </h1>

      <form onSubmit={handleSubmit}>
        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-schedule-title">
            {__('Wat', 'etm')}
          </label>
          <input
            id="etm-schedule-title"
            class="etm-form-input"
            type="text"
            value={title}
            onInput={(e) => setTitle(e.target.value)}
            placeholder={__('Bijv. Hockeytraining', 'etm')}
            autofocus
            required
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Herhaling', 'etm')}</label>
          <div class="etm-priority-selector">
            <button
              type="button"
              class={`etm-priority-btn${kind === 'weekly' ? ' active' : ''}`}
              onClick={() => setKind('weekly')}
            >
              {__('Wekelijks', 'etm')}
            </button>
            <button
              type="button"
              class={`etm-priority-btn${kind === 'once' ? ' active' : ''}`}
              onClick={() => setKind('once')}
            >
              {__('Eenmalig', 'etm')}
            </button>
          </div>
        </div>

        {kind === 'weekly' ? (
          <>
            <div class="etm-form-group">
              <label class="etm-form-label">{__('Op welke dagen', 'etm')}</label>
              <div class="etm-weekday-selector">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.bit}
                    type="button"
                    class={`etm-weekday-btn${hasWeekday(weekdays, day.bit) ? ' active' : ''}`}
                    aria-pressed={hasWeekday(weekdays, day.bit) ? 'true' : 'false'}
                    aria-label={day.long()}
                    onClick={() => setWeekdays(toggleWeekday(weekdays, day.bit))}
                  >
                    {day.short()}
                  </button>
                ))}
              </div>
              {weekdays ? <p class="etm-field-hint">{weekdaysSummary(weekdays)}</p> : null}
            </div>

            <div class="etm-form-row">
              <div class="etm-form-group">
                <label class="etm-form-label" for="etm-schedule-start">
                  {__('Van', 'etm')}
                </label>
                <input
                  id="etm-schedule-start"
                  class="etm-form-input"
                  type="time"
                  value={timeStart}
                  onInput={(e) => setTimeStart(e.target.value)}
                />
              </div>
              <div class="etm-form-group">
                <label class="etm-form-label" for="etm-schedule-end">
                  {__('Tot', 'etm')}
                </label>
                <input
                  id="etm-schedule-end"
                  class="etm-form-input"
                  type="time"
                  value={timeEnd}
                  onInput={(e) => setTimeEnd(e.target.value)}
                />
              </div>
            </div>
            <p class="etm-field-hint">{__('Laat de tijd leeg als het de hele dag geldt.', 'etm')}</p>
          </>
        ) : (
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-schedule-occurs">
              {__('Wanneer', 'etm')}
            </label>
            <input
              id="etm-schedule-occurs"
              class="etm-form-input"
              type="datetime-local"
              value={occursAt}
              onInput={(e) => setOccursAt(e.target.value)}
            />
          </div>
        )}

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Voor wie', 'etm')}</label>
          <div class="etm-person-picker">
            <button
              type="button"
              class={`etm-person-chip${personId === '' ? ' active' : ''}`}
              onClick={() => setPersonId('')}
            >
              {__('Hele huishouden', 'etm')}
            </button>
            {people.map((person) => (
              <button
                key={person.id}
                type="button"
                class={`etm-person-chip etm-person-chip--${person.color_role}${
                  String(personId) === String(person.id) ? ' active' : ''
                }`}
                onClick={() => setPersonId(String(person.id))}
              >
                {person.name}
              </button>
            ))}
          </div>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Icoon', 'etm')}</label>
          <div class="etm-icon-choice-grid">
            {SCHEDULE_ICON_CHOICES.map((choice) => (
              <button
                key={choice}
                type="button"
                class={`etm-icon-choice${icon === choice ? ' active' : ''}`}
                aria-label={choice}
                onClick={() => setIcon(choice)}
              >
                <Icon name={choice} size={22} />
              </button>
            ))}
          </div>
        </div>

        <details class="etm-collapsible">
          <summary>
            {__('Meer opties', 'etm')}
            <Icon name="keyboard_arrow_down" size={18} />
          </summary>

          {kind === 'weekly' ? (
            <div class="etm-form-row">
              <div class="etm-form-group">
                <label class="etm-form-label" for="etm-schedule-from">
                  {__('Alleen vanaf', 'etm')}
                </label>
                <input
                  id="etm-schedule-from"
                  class="etm-form-input"
                  type="date"
                  value={activeFrom}
                  onInput={(e) => setActiveFrom(e.target.value)}
                />
              </div>
              <div class="etm-form-group">
                <label class="etm-form-label" for="etm-schedule-until">
                  {__('Tot en met', 'etm')}
                </label>
                <input
                  id="etm-schedule-until"
                  class="etm-form-input"
                  type="date"
                  value={activeUntil}
                  onInput={(e) => setActiveUntil(e.target.value)}
                />
                {/* Both bounds are inclusive — "tot en met" is the label
                    precisely because that is what the backend does. */}
                <p class="etm-field-hint">{__('Bijv. een sportseizoen dat in mei stopt.', 'etm')}</p>
              </div>
            </div>
          ) : null}

          <div class="etm-form-group">
            <label class="etm-form-label">{__('Waarschuwing vooraf', 'etm')}</label>
            <div class="etm-priority-selector">
              {WARN_OPTIONS.map((option) => (
                <button
                  key={String(option.value)}
                  type="button"
                  class={`etm-priority-btn${String(warnMinutes) === String(option.value) ? ' active' : ''}`}
                  onClick={() => setWarnMinutes(option.value === '' ? '' : String(option.value))}
                >
                  {option.label()}
                </button>
              ))}
            </div>
            <p class="etm-field-hint">{__('Wordt gebruikt zodra het weekritme in de herinneringsmail staat.', 'etm')}</p>
          </div>

          {!isEdit && household.value ? (
            <div class="etm-form-group etm-checkbox-group">
              <input
                id="etm-schedule-shared"
                type="checkbox"
                checked={isShared}
                onChange={(e) => setIsShared(e.currentTarget.checked)}
              />
              <label for="etm-schedule-shared">{__('Gedeeld met huishouden', 'etm')}</label>
            </div>
          ) : null}
        </details>

        <button type="submit" class="etm-btn etm-btn-primary" disabled={saving}>
          {isEdit ? __('Opslaan', 'etm') : __('Item toevoegen', 'etm')}
        </button>
      </form>

      {isEdit ? (
        <button type="button" class="etm-btn etm-btn-danger" disabled={saving} onClick={handleDelete}>
          <Icon name="delete" size={20} />
          {__('Verwijderen', 'etm')}
        </button>
      ) : null}
    </div>
  );
}
